import { IconChevronLeftLarge, IconClose } from '@hubble/icons';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import React__default, { useContext } from 'react';
import { Button } from '../../Button/index.js';
import { Flex } from '../../Flex/index.js';
import { Content } from '../../shared/Content/index.js';
import { Footer } from '../../shared/Footer/index.js';
import { ModalHeader } from '../../shared/Header/index.js';
import { Text } from '../../Text/index.js';
import { ModalRootContext, ModalHeaderContext } from '../shared/index.js';
import { OverlayBase, ContainerWrapper, ContainerBase } from '../shared/styles.js';
import { jsxs, Fragment, jsx } from '@emotion/react/jsx-runtime';

const Root = ({
  children,
  onBack,
  onClose,
  disableAnimation = false,
  ...props
}) => jsx(DialogPrimitive.Root, {
  onOpenChange: open => {
    if (!open) {
      onClose === null || onClose === void 0 ? void 0 : onClose();
    }
  },
  ...props,
  children: jsx(ModalRootContext.Provider, {
    value: {
      onBack,
      disableAnimation
    },
    children: children
  })
});
const Portal = DialogPrimitive.Portal;
const Container = React__default.forwardRef(({
  children,
  ...props
}, ref) => {
  const {
    onBack,
    disableAnimation
  } = useContext(ModalRootContext);
  return jsxs(Fragment, {
    children: [jsx(OverlayBase, {
      as: DialogPrimitive.Overlay,
      disableAnimation: disableAnimation || Boolean(onBack)
    }), jsx(ContainerWrapper, {
      as: DialogPrimitive.Overlay,
      disableAnimation: disableAnimation || Boolean(onBack),
      children: jsx(ContainerBase, {
        ref: ref,
        as: DialogPrimitive.Content,
        disableAnimation: disableAnimation || Boolean(onBack),
        "data-variant": "dialog",
        style: {
          '--transform-origin': 'center center'
        },
        ...props,
        children: children
      })
    })]
  });
});
const Title = props => {
  const {
    variant
  } = useContext(ModalHeaderContext);
  return jsx(DialogPrimitive.Title, {
    asChild: true,
    children: jsx(Text.Heading, {
      ...props,
      size: variant === 'inline' ? 'xxs' : 'md',
      center: variant === 'center'
    })
  });
};
const Description = props => {
  const {
    variant
  } = useContext(ModalHeaderContext);
  return jsx(DialogPrimitive.Description, {
    asChild: true,
    children: jsx(Text.Body, {
      ...props,
      size: "sm",
      as: "div",
      center: variant === 'center'
    })
  });
};
const Close = ({
  children,
  ...props
}) => jsx(DialogPrimitive.Close, {
  asChild: true,
  ...props,
  children: children
});
const VisualElement = ({
  children
}) => {
  const {
    variant
  } = useContext(ModalHeaderContext);
  return jsx(Flex, {
    mb: variant === 'inline' ? 0 : 1,
    children: children
  });
};
const Trigger = ({
  children,
  ...props
}) => jsx(DialogPrimitive.Trigger, {
  asChild: true,
  ...props,
  children: children
});
const Header = ({
  children,
  variant
}) => {
  const {
    onBack
  } = useContext(ModalRootContext);
  return jsx(ModalHeaderContext.Provider, {
    value: {
      variant
    },
    children: jsx(ModalHeader, {
      variant: variant,
      back: onBack && jsx(Flex, {
        children: jsx(Button.Secondary, {
          size: "sm",
          icon: jsx(IconChevronLeftLarge, {}),
          "aria-label": "Back",
          onClick: onBack
        })
      }),
      close: jsx(Close, {
        children: jsx(Button.Secondary, {
          size: "sm",
          icon: jsx(IconClose, {}),
          "aria-label": "Close"
        })
      }),
      children: children
    })
  });
};
const Dialog = Object.assign(Root, {
  Trigger,
  Header,
  Footer,
  Portal,
  Container,
  Content,
  Close,
  VisualElement,
  Title,
  Description,
  displayName: 'Dialog'
});
Trigger.displayName = 'Dialog.Trigger';
Header.displayName = 'Dialog.Header';
Footer.displayName = 'Dialog.Footer';
Portal.displayName = 'Dialog.Portal';
Container.displayName = 'Dialog.Container';
Content.displayName = 'Dialog.Content';
Close.displayName = 'Dialog.Close';
VisualElement.displayName = 'Dialog.VisualElement';
Title.displayName = 'Dialog.Title';
Description.displayName = 'Dialog.Description';

export { Close, Dialog };
